import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, Loader2, Undo2, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import { useGuestIdentity } from "../lib/guestIdentity";
import { checkInErrorMessage, checkInToEvent, checkOutFromEvent } from "../lib/checkIn";
import { StickerButton } from "../components/Sticker";
import { activeCheckInPath, clearActiveCheckIn, setActiveCheckIn, clearLiveBannerSettled } from "../lib/activeCheckIn";

const SIZE_CLASSES = {
  sm: "px-4 py-2.5 text-[13px] min-h-[44px]",
  md: "px-5 py-3 text-[15px] min-h-[44px]",
  lg: "px-7 py-4 text-[17px] min-h-[56px]",
};

/** Check in, persist local floor state, then open the event check-in URL. */
export async function checkInAndGoToFloor({
  eventId,
  occurrenceDate,
  publicPath = null,
  dancerId,
  navigate,
  from = "/events",
  onCheckedIn,
}) {
  if (!dancerId) {
    toast.error("RSVP first so we know who you are.");
    return null;
  }
  const result = await checkInToEvent(eventId, { dancerId, occurrenceDate });
  if (result.already_checked_in) {
    toast.success("You're already checked in!");
  } else {
    toast.success("Checked in — see you on the floor!");
    clearLiveBannerSettled(eventId, result.rsvp?.occurrence_date || occurrenceDate);
  }
  const occ = result.rsvp?.occurrence_date || occurrenceDate;
  setActiveCheckIn({
    event_id: eventId,
    dancer_id: dancerId,
    occurrence_date: occ,
    checked_in_at: result.rsvp?.checked_in_at,
    public_path: publicPath,
  });
  onCheckedIn?.(result);
  const path = activeCheckInPath({
    event_id: eventId,
    occurrence_date: occ,
    public_path: publicPath,
  });
  if (path && navigate) navigate(path, { state: { from } });
  return result;
}

export default function CheckInButton({
  eventId,
  occurrenceDate,
  publicPath = null,
  checkedInAt = null,
  onCheckedIn,
  size = "md",
  color = "pink",
  plain = false,
  glass = false,
  className = "",
  checkedInLabel = "Undo check-in",
  checkoutAriaLabel = null,
  undoingLabel = "Undoing…",
  testId = "check-in-btn",
}) {
  const [loading, setLoading] = useState(false);
  const { dancerId } = useGuestIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const from = `${location.pathname}${location.search}`;

  async function performCheckIn() {
    setLoading(true);
    try {
      await checkInAndGoToFloor({
        eventId,
        occurrenceDate,
        publicPath,
        dancerId,
        navigate,
        from,
        onCheckedIn,
      });
    } catch (err) {
      toast.error(checkInErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleUndo() {
    if (!dancerId) return;
    const isCheckout = checkedInLabel === "Check out";
    setLoading(true);
    try {
      const result = await checkOutFromEvent(eventId, { dancerId, occurrenceDate });
      toast.success(isCheckout ? "You're checked out." : "Check-in removed.");
      clearActiveCheckIn(eventId);
      onCheckedIn?.(result);
      navigate("/events");
    } catch (err) {
      toast.error(checkInErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const isCheckout = checkedInLabel === "Check out";
  const CheckedInIcon = isCheckout ? DoorOpen : Undo2;
  const busyCheckedInLabel = loading
    ? (isCheckout ? "Checking out…" : undoingLabel)
    : checkedInLabel;

  if (checkedInAt) {
    if (plain) {
      const plainTone = glass
        ? "bg-white/15 backdrop-blur-sm border border-white/30 text-cream hover:bg-white/25"
        : "bg-ink text-white";
      const plainShell = glass
        ? "inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-md2 font-ui font-semibold text-[13px] transition-colors"
        : "rounded-btn border border-black/[0.08] px-3 py-1.5 inline-flex items-center gap-1.5 min-h-[44px] font-ui font-medium text-[13px] bg-white hover:bg-canvas transition-colors";
      return (
        <button
          type="button"
          onClick={handleUndo}
          disabled={loading}
          aria-busy={loading || undefined}
          aria-label={checkoutAriaLabel || busyCheckedInLabel}
          data-testid={`${testId}-undo`}
          className={`${plainShell} disabled:opacity-50 disabled:cursor-not-allowed ${plainTone} ${className}`}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
          ) : (
            <CheckedInIcon className="w-4 h-4" strokeWidth={3} aria-hidden />
          )}
          {busyCheckedInLabel}
        </button>
      );
    }

    return (
      <StickerButton
        color={color}
        size={size}
        onClick={handleUndo}
        disabled={loading}
        aria-busy={loading || undefined}
        data-testid={`${testId}-undo`}
        className={className}
      >
        <span className="inline-flex items-center gap-2">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
          ) : (
            <CheckedInIcon className="w-4 h-4" strokeWidth={3} aria-hidden />
          )}
          {busyCheckedInLabel}
        </span>
      </StickerButton>
    );
  }

  return (
    <button
      type="button"
      onClick={performCheckIn}
      disabled={loading}
      aria-busy={loading || undefined}
      data-testid={testId}
      className={`check-in-cta check-in-gradient font-ui font-semibold rounded-btn inline-flex items-center justify-center disabled:cursor-not-allowed ${SIZE_CLASSES[size] || SIZE_CLASSES.md} ${className}`}
    >
      <span className="check-in-cta__label inline-flex items-center gap-2">
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
        ) : (
          <MapPin className="w-4 h-4" strokeWidth={3} />
        )}
        {loading ? "Checking in…" : "Check in"}
      </span>
    </button>
  );
}
